import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    Avatar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    CircularProgress,
    Card
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import FaceIcon from '@mui/icons-material/Face';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import InsightsIcon from '@mui/icons-material/Insights';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import BusinessIcon from '@mui/icons-material/Business';
import AdminOSLayout from '../../../components/layout/AdminOSLayout';
import api from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { designTokens } from '../../../theme/designTokens';

interface Learner {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    organizationId: {
        _id: string;
        name: string;
    };
    preferences: {
        photoUrl: string;
    };
    lastLogin: string | null;
    totalTimeSpent: number;
}

const LearnerListPage = () => {
    const { user } = useAuth();
    const [learners, setLearners] = useState<Learner[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { showToast } = useToast();
    const [totalLearners, setTotalLearners] = useState(0);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();

    // Create learner dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        fetchLearners();
    }, [page, rowsPerPage, search]);

    const fetchLearners = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/learners', {
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    search
                }
            });
            setLearners(res.data.learners);
            setTotalLearners(res.data.totalLearners);
        } catch (err) {
            console.error('Error fetching learners', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    const handleOpenCreateDialog = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
    };

    const handleCreateLearner = async () => {
        if (!firstName || !lastName || !email) {
            showToast('Veuillez remplir tous les champs', 'warning');
            return;
        }

        setCreating(true);
        try {
            await api.post('/api/users', {
                firstName,
                lastName,
                email,
                role: 'learner',
                organizationId: user?.organizationId
            });

            fetchLearners();
            handleCloseCreateDialog();
            showToast('Apprenant créé avec succès ! Un email a été envoyé pour définir le mot de passe.', 'success');
        } catch (err: any) {
            console.error("Error creating learner", err);
            showToast(err.response?.data?.message || 'Erreur lors de la création de l\'apprenant', 'error');
        } finally {
            setCreating(false);
        }
    };

    // Calculate quick stats (derived from current view or total)
    // Note: detailed stats might need a dedicated endpoint, but we can show Total
    const activeLearnersCount = learners.filter(l => l.lastLogin).length; // Just a visual proxy for the current page

    const handleResendToken = async (learnerId: string) => {
        try {
            const res = await api.post(`/api/users/${learnerId}/resend-token`);
            showToast(res.data.message || 'Lien de connexion renvoyé', 'success');
        } catch (err: any) {
            console.error("Error resending token", err);
            showToast(err.response?.data?.message || 'Erreur lors du renvoi du lien', 'error');
        }
    };

    return (
        <AdminOSLayout>
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
                p: { xs: 2, md: 4 }
            }}>
                {/* Hero Header */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        borderRadius: 8,
                        p: { xs: 4, md: 5 },
                        mb: 4,
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(79, 70, 229, 0.15)'
                    }}
                >
                    {/* Decorative elements */}
                    <Box sx={{
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 70%)',
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        bottom: -50,
                        left: '20%',
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%)',
                    }} />

                    <Grid container spacing={4} alignItems="center">
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                                <Avatar sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    width: 70,
                                    height: 70,
                                    backdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                                }}>
                                    <GroupIcon sx={{ fontSize: 35 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, fontFamily: 'Quicksand, sans-serif', letterSpacing: -1 }}>
                                        Gestion Apprenants
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500, maxWidth: 500 }}>
                                        Suivez l'activité, la progression et gérez les comptes de vos étudiants en temps réel.
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreateDialog}
                                sx={{
                                    bgcolor: 'white',
                                    color: '#4f46e5',
                                    fontWeight: 800,
                                    px: 4,
                                    py: 2,
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    fontFamily: 'Quicksand',
                                    boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.95)',
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 15px 30px rgba(0,0,0,0.2)',
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                Ajouter Apprenant
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(16px)',
                                p: 3,
                                borderRadius: 6,
                                border: '1px solid rgba(255,255,255,0.2)',
                                boxShadow: '0 12px 40px rgba(0,0,0,0.1)'
                            }}>
                                <Grid container spacing={3} color="white">
                                    <Grid size={{ xs: 6 }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h2" sx={{ fontWeight: 900, fontFamily: 'Quicksand' }}>{totalLearners}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>Inscrits</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h2" sx={{ fontWeight: 900, fontFamily: 'Quicksand' }}>{activeLearnersCount}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>Actifs (Page)</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Box sx={{
                                            mt: 1,
                                            p: 1.5,
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            borderRadius: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            justifyContent: 'center'
                                        }}>
                                            <InsightsIcon sx={{ fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Taux de connexion : {totalLearners > 0 ? Math.round((activeLearnersCount / totalLearners) * 100) : 0}%</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Search & Filter Bar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1,
                        mb: 4,
                        borderRadius: 5,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                        border: '1px solid #e2e8f0',
                        bgcolor: 'rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Rechercher par nom, prénom ou email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#4f46e5', ml: 1 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 4,
                                bgcolor: 'transparent',
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: 'none' },
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#1e293b'
                            }
                        }}
                    />
                    <Box sx={{ borderLeft: '1px solid #e2e8f0', height: 40, mx: 1 }} />
                    <IconButton sx={{
                        bgcolor: '#f1f5f9',
                        color: '#64748b',
                        borderRadius: 3,
                        mr: 1,
                        '&:hover': { bgcolor: '#e2e8f0' }
                    }}>
                        <FilterListIcon />
                    </IconButton>
                </Paper>

                {/* Table Card */}
                <Card sx={{
                    borderRadius: 6,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.04)',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    bgcolor: 'white'
                }}>
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3, bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FaceIcon sx={{ fontSize: 18 }} /> Apprenant
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3, bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1, display: { xs: 'none', md: 'table-cell' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AlternateEmailIcon sx={{ fontSize: 18 }} /> Email
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3, bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1, display: { xs: 'none', lg: 'table-cell' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BusinessIcon sx={{ fontSize: 18 }} /> Organisme
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3, bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HistoryIcon sx={{ fontSize: 18 }} /> Activité
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3, bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1, display: { xs: 'none', lg: 'table-cell' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InsightsIcon sx={{ fontSize: 18, color: '#4f46e5' }} /> Temps total
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', py: 3, bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {learners.map((learner) => (
                                    <TableRow
                                        key={learner._id}
                                        hover
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'rgba(79, 70, 229, 0.04)',
                                                '& .view-icon': { opacity: 1, transform: 'scale(1.1)' }
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <TableCell sx={{ py: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                                <Avatar
                                                    src={learner.preferences?.photoUrl}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                                        color: 'white',
                                                        fontWeight: 800,
                                                        width: 48,
                                                        height: 48,
                                                        fontSize: '1rem',
                                                        fontFamily: 'Quicksand',
                                                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
                                                    }}
                                                >
                                                    {learner.firstName[0]}{learner.lastName[0]}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', fontFamily: 'Quicksand' }}>
                                                        {learner.firstName} {learner.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: { xs: 'block', md: 'none' } }}>
                                                        {learner.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>{learner.email}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, display: { xs: 'none', lg: 'table-cell' } }}>
                                            <Chip
                                                label={learner.organizationId?.name || 'Individuel'}
                                                size="small"
                                                sx={{
                                                    borderRadius: 2,
                                                    bgcolor: '#f1f5f9',
                                                    color: '#475569',
                                                    fontWeight: 700,
                                                    fontSize: '0.7rem',
                                                    border: '1px solid #e2e8f0'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, display: { xs: 'none', sm: 'table-cell' } }}>
                                            {learner.lastLogin ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>En ligne</Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                                                        {format(new Date(learner.lastLogin), 'Pp', { locale: fr })}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, bgcolor: '#f8fafc', px: 1.5, py: 0.5, borderRadius: 2 }}>Jamais connecté</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, display: { xs: 'none', lg: 'table-cell' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#4f46e5' }}>
                                                {formatTime(learner.totalTimeSpent)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                {!learner.lastLogin && (
                                                    <IconButton
                                                        onClick={() => handleResendToken(learner._id)}
                                                        title="Renvoyer le lien de connexion"
                                                        sx={{
                                                            color: '#0ea5e9',
                                                            bgcolor: '#f0f9ff',
                                                            '&:hover': {
                                                                bgcolor: '#0ea5e9',
                                                                color: 'white'
                                                            },
                                                            borderRadius: 3,
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        <AlternateEmailIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    className="view-icon"
                                                    onClick={() => navigate(`/admin/apprenants/${learner._id}`)}
                                                    sx={{
                                                        color: '#4f46e5',
                                                        bgcolor: '#eff6ff',
                                                        '&:hover': {
                                                            bgcolor: '#4f46e5',
                                                            color: 'white'
                                                        },
                                                        borderRadius: 3,
                                                        opacity: 0.8,
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>

                                    </TableRow>
                                ))}
                                {learners.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                            <FaceIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary" gutterBottom>Aucun apprenant trouvé</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Commencez par ajouter un nouvel apprenant à votre organisation
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalLearners}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Lignes par page"
                        sx={{
                            borderTop: '1px solid #e2e8f0',
                            bgcolor: '#f8fafc',
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontWeight: 700,
                                color: '#64748b',
                                fontFamily: 'Quicksand'
                            },
                            '& .MuiTablePagination-select': {
                                fontWeight: 800,
                                color: '#4f46e5'
                            }
                        }}
                    />
                </Card>

                {/* Create Learner Dialog */}
                <Dialog
                    open={createDialogOpen}
                    onClose={handleCloseCreateDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 6,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: 'white',
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <AddIcon />
                            <Typography variant="h5" sx={{ fontWeight: 900, fontFamily: 'Quicksand' }}>
                                Ajouter un Apprenant
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseCreateDialog} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 4, mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 4, color: '#64748b', fontWeight: 600 }}>
                            Remplissez les informations ci-dessous pour créer un nouveau profil apprenant. Un email d'invitation lui sera envoyé automatiquement.
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Prénom"
                                    placeholder="Ex: Jean"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    sx={{
                                        ...designTokens.input,
                                        '& .MuiInputLabel-root': { fontWeight: 700, fontFamily: 'Quicksand' },
                                        '& .MuiOutlinedInput-root': { borderRadius: 3 }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nom"
                                    placeholder="Ex: Dupont"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    sx={{
                                        ...designTokens.input,
                                        '& .MuiInputLabel-root': { fontWeight: 700, fontFamily: 'Quicksand' },
                                        '& .MuiOutlinedInput-root': { borderRadius: 3 }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    placeholder="jean.dupont@exemple.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    sx={{
                                        ...designTokens.input,
                                        '& .MuiInputLabel-root': { fontWeight: 700, fontFamily: 'Quicksand' },
                                        '& .MuiOutlinedInput-root': { borderRadius: 3 }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AlternateEmailIcon sx={{ color: '#4f46e5', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 4, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                        <Button
                            onClick={handleCloseCreateDialog}
                            disabled={creating}
                            sx={{
                                color: '#64748b',
                                fontWeight: 800,
                                textTransform: 'none',
                                fontFamily: 'Quicksand',
                                px: 3
                            }}
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleCreateLearner}
                            variant="contained"
                            disabled={creating}
                            startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                            sx={{
                                bgcolor: '#4f46e5',
                                borderRadius: 3,
                                fontWeight: 800,
                                px: 4,
                                py: 1.2,
                                textTransform: 'none',
                                fontFamily: 'Quicksand',
                                fontSize: '1rem',
                                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
                                '&:hover': { bgcolor: '#4338ca', transform: 'translateY(-2px)' },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {creating ? 'Création...' : 'Créer le profil'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminOSLayout>
    );
};

export default LearnerListPage;
